import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import { ColumnDef } from '@tanstack/react-table';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserRoles } from '@/constant';
import HarborRequestModal from './HarborRequestModal';
import { HarborAssociationDetailsModal } from './HarborAssociationDetailsModal';
import {
  useGetHarborAssociationsQuery,
  useCreateHarborAssociationsMutation,
  useCancelHarborAssociationMutation,
  useApproveHarborClubRequestMutation,
  useRevokeHarborClubRequestMutation,
  HarborAssociationRequest,
} from '@/services/HarborAssociationService';
import { formatDisplayDate } from '@/utils/Date';

interface HarborAssociationListingProps {
  clubId?: string;
}

export const HarborAssociationListing = ({
  clubId,
}: HarborAssociationListingProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const userData = useSelector((state: RootState) => state.auth.userData);

  // Check if current user is yacht club admin
  const isYachtClubAdmin = userData?.roles?.includes(
    UserRoles.YACHT_CLUB_ADMIN
  );
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedAssociation, setSelectedAssociation] =
    useState<HarborAssociationRequest | null>(null);

  const {
    pagination,
    setPagination,
    handleTotalPages,
    handleTotalItems,
    resetSearch,
    totalPages,
    totalItems,
  } = usePagination();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const canCreateHarborAssociationRequest = hasAnyPermission([
    'harbor_association_management_create',
    'harbor_association_management_update',
  ]);

  const canUpdateHarborAssociationRequest = hasPermission(
    'harbor_association_management_update'
  );

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setPagination((prev) => {
      if (prev.pageIndex === 0) return prev;
      return {
        ...prev,
        pageIndex: 0,
      };
    });
  }, [debouncedSearchTerm, statusFilter, setPagination]);

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
      status: statusFilter || undefined,
      club_id: clubId,
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      debouncedSearchTerm,
      statusFilter,
      clubId,
    ]
  );

  const {
    data: associationData,
    isLoading: isAssociationsLoading,
    error: associationError,
    refetch,
    isFetching,
  } = useGetHarborAssociationsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    skip: false,
  });

  // Update pagination totals when data changes
  useEffect(() => {
    if (associationData) {
      handleTotalPages(
        associationData.total_pages || associationData.totalPages || 1
      );
      handleTotalItems(associationData.totalItems || 0);
    }
  }, [associationData, handleTotalPages, handleTotalItems]);

  const [createAssociations, { isLoading: isCreating }] =
    useCreateHarborAssociationsMutation();
  const [cancelAssociation, { isLoading: isCancelling }] =
    useCancelHarborAssociationMutation();
  const [approveRequest, { isLoading: isApproving }] =
    useApproveHarborClubRequestMutation();
  const [revokeRequest, { isLoading: isRevoking }] =
    useRevokeHarborClubRequestMutation();

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value);
    },
    []
  );

  const handleFilterReset = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    resetSearch();
  }, [resetSearch]);

  const handleNewRequest = useCallback(
    async (selectedHarborIds: string[], requestMessage: string) => {
      if (selectedHarborIds.length === 0) {
        enqueueSnackbar('No harbors selected', { variant: 'warning' });
        return;
      }

      try {
        await createAssociations({
          harbor_ids: selectedHarborIds,
          request_message: requestMessage || 'Request for harbor association',
          club_id: clubId,
        }).unwrap();

        enqueueSnackbar(
          `New harbor association request created for ${selectedHarborIds.length} harbor(s)`,
          { variant: 'success' }
        );
        setIsRequestModalOpen(false);
        refetch();
      } catch {
        enqueueSnackbar(
          'Failed to create harbor association request. Please try again.',
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar, createAssociations, refetch, clubId]
  );

  const columns: ColumnDef<HarborAssociationRequest>[] = useMemo(
    () => [
      {
        accessorKey: 'harbor.name',
        header: 'Harbor Name',
        cell: ({ row }) => (
          <div className="text-gray-800 font-medium">
            {row.original.harbor.name}
          </div>
        ),
        meta: {
          className: 'min-w-[180px]',
        },
      },
      {
        accessorKey: 'harbor.location',
        header: 'Harbor Location',
        cell: ({ row }) => (
          <div className="text-gray-700">
            {row.original.harbor.location ? row.original.harbor.location : '-'}
          </div>
        ),
        meta: {
          className: 'min-w-[150px]',
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          let statusClass = '';
          let statusText = '';

          switch (status) {
            case 'pending':
              statusClass =
                'bg-yellow-100 dark:bg-yellow-900 contrast:bg-yellow-900 text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200';
              statusText = 'Pending';
              break;
            case 'approved':
              statusClass =
                'bg-green-100 dark:bg-green-900 contrast:bg-green-900 text-green-800 dark:text-green-200 contrast:text-green-200';
              statusText = 'Approved';
              break;
            case 'rejected':
              statusClass =
                'bg-red-100 dark:bg-red-900 contrast:bg-red-900 text-red-800 dark:text-red-200 contrast:text-red-200';
              statusText = 'Rejected';
              break;
            case 'revoked':
              statusClass =
                'bg-orange-100 dark:bg-orange-900 contrast:bg-orange-900 text-orange-800 dark:text-orange-200 contrast:text-orange-200';
              statusText = 'Revoked';
              break;
            case 'approved_by_admin':
              statusClass =
                'bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-200';
              statusText = 'Approved by Admin';
              break;
            case 'club_approval_pending':
              statusClass =
                'bg-purple-100 dark:bg-purple-900 contrast:bg-purple-900 text-purple-800 dark:text-purple-200 contrast:text-purple-200';
              statusText = 'Club Approval Pending';
              break;
            default:
              statusClass = 'bg-gray-100 text-gray-800';
              statusText = 'Unknown';
              break;
          }

          return (
            <div className="flex justify-start">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusClass}`}
                data-testid="status-badge"
                role="status"
                aria-label={`Request status: ${statusText}`}
              >
                {statusText}
              </span>
            </div>
          );
        },
        meta: {
          className: 'min-w-[160px] w-[160px]',
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Request Date',
        cell: ({ row }) => {
          return (
            <div className="text-gray-600">
              {formatDisplayDate(row.original.created_at, '-')}
            </div>
          );
        },
        meta: {
          className: 'min-w-[120px]',
        },
      },
      {
        accessorKey: 'request_message',
        header: 'Message',
        cell: ({ row }) => (
          <div
            className="text-gray-700 max-w-[200px] truncate"
            title={row.original.request_message || ''}
          >
            {row.original.request_message || '-'}
          </div>
        ),
        meta: {
          className: 'min-w-[200px]',
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const request = row.original;

          return (
            <div className="flex items-center justify-end gap-2">
              <button
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="View details"
                onClick={() => {
                  setSelectedAssociation(request);
                  setIsDetailsModalOpen(true);
                }}
              >
                <KeenIcon
                  icon="eye"
                  className="text-gray-500 hover:text-blue-600"
                />
              </button>

              {/* For club_approval_pending status - show approve and cancel buttons for yacht club admin */}
              {request.status === 'club_approval_pending' &&
                isYachtClubAdmin && (
                  <>
                    <button
                      className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Approve request"
                      disabled={isApproving}
                      onClick={async () => {
                        try {
                          await approveRequest({
                            request_id: request.id,
                            response_message: 'Approved by club admin',
                          }).unwrap();
                          enqueueSnackbar(
                            `Request for ${request.harbor.name} approved successfully`,
                            { variant: 'success' }
                          );
                          refetch();
                        } catch (error) {
                          console.error('Error approving request:', error);
                          enqueueSnackbar(
                            `Failed to approve request for ${request.harbor.name}`,
                            { variant: 'error' }
                          );
                        }
                      }}
                    >
                      <KeenIcon
                        icon="check"
                        className="text-gray-500 hover:text-green-600"
                      />
                    </button>
                    <button
                      className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Reject request"
                      disabled={isCancelling}
                      onClick={async () => {
                        try {
                          await cancelAssociation({
                            request_id: request.id,
                            action: 'reject',
                            reason: 'Rejected by club admin',
                          }).unwrap();
                          enqueueSnackbar(
                            `Request for ${request.harbor.name} rejected successfully`,
                            { variant: 'success' }
                          );
                          refetch();
                        } catch (error) {
                          console.error('Error rejecting request:', error);
                          enqueueSnackbar(
                            `Failed to reject request for ${request.harbor.name}`,
                            { variant: 'error' }
                          );
                        }
                      }}
                    >
                      <KeenIcon
                        icon="cross"
                        className="text-gray-500 hover:text-red-600"
                      />
                    </button>
                  </>
                )}

              {/* For approved status - show revoke button */}
              {request.status === 'approved' &&
                canUpdateHarborAssociationRequest && (
                  <button
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Revoke association"
                    disabled={isRevoking}
                    onClick={async () => {
                      try {
                        await revokeRequest({
                          request_id: request.id,
                          reason: 'Revoked by club admin',
                        }).unwrap();
                        enqueueSnackbar(
                          `Association with ${request.harbor.name} revoked successfully`,
                          { variant: 'success' }
                        );
                        refetch();
                      } catch (error) {
                        console.error('Error revoking association:', error);
                        enqueueSnackbar(
                          `Failed to revoke association with ${request.harbor.name}`,
                          { variant: 'error' }
                        );
                      }
                    }}
                  >
                    <KeenIcon
                      icon="arrows-circle"
                      className="text-gray-500 hover:text-orange-600"
                    />
                  </button>
                )}

              {/* For pending status - show withdraw button */}
              {request.status === 'pending' &&
                canUpdateHarborAssociationRequest && (
                  <button
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Withdraw request"
                    disabled={isCancelling}
                    onClick={async () => {
                      try {
                        await cancelAssociation({
                          request_id: request.id,
                          action: 'withdraw',
                          reason: 'Withdrawn by club admin',
                        }).unwrap();
                        enqueueSnackbar(
                          `Request for ${request.harbor.name} withdrawn successfully`,
                          { variant: 'success' }
                        );
                        refetch();
                      } catch (error) {
                        console.error('Error withdrawing request:', error);
                        enqueueSnackbar(
                          `Failed to withdraw request for ${request.harbor.name}`,
                          { variant: 'error' }
                        );
                      }
                    }}
                  >
                    <KeenIcon
                      icon="cross"
                      className="text-gray-500 hover:text-red-600"
                    />
                  </button>
                )}
            </div>
          );
        },
        meta: {
          className: 'w-[80px]',
        },
      },
    ],
    [
      enqueueSnackbar,
      cancelAssociation,
      approveRequest,
      revokeRequest,
      refetch,
      isCancelling,
      isApproving,
      isRevoking,
      isYachtClubAdmin,
      canUpdateHarborAssociationRequest,
    ]
  );

  const hasActiveFilters = Boolean(searchTerm || statusFilter);
  const hasData = associationData?.data && associationData.data.length > 0;
  const showEmptyState =
    !isAssociationsLoading && !hasData && !associationError;

  return (
    <Container>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <label
            className="input input-sm h-8 flex items-center border border-gray-300 rounded-md"
            htmlFor="search-harbors"
          >
            <KeenIcon
              type="filled"
              icon="magnifier"
              className="text-gray-500"
            />
            <input
              id="search-harbors"
              placeholder="Search harbors"
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full h-8 text-sm"
              aria-label="Search harbors"
            />
          </label>
          <select
            className="select select-sm h-8 border border-gray-300 rounded-md"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="club_approval_pending">Club Approval Pending</option>
            <option value="approved">Approved</option>
            <option value="approved_by_admin">Approved by Admin</option>
            <option value="rejected">Rejected</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>

        {canCreateHarborAssociationRequest && (
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setIsRequestModalOpen(true)}
          >
            New Harbor Request
          </button>
        )}
      </div>

      {associationError ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <KeenIcon
                icon="information-circle"
                className="h-5 w-5 text-red-400"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading harbor association requests
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>There was an error fetching the data. Please try again.</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="btn btn-sm btn-light"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : showEmptyState ? (
        <div className="bg-gray-100 border border-gray-200 rounded-md p-6 text-center">
          <p className="text-gray-600">
            {hasActiveFilters
              ? 'No harbor association requests match your filters'
              : 'No harbor association requests found'}
          </p>
          {hasActiveFilters ? (
            <button
              type="button"
              className="mt-4 btn btn-sm btn-light"
              onClick={handleFilterReset}
            >
              Clear Filters
            </button>
          ) : canCreateHarborAssociationRequest ? (
            <button
              type="button"
              className="mt-4 btn btn-sm btn-light"
              onClick={() => setIsRequestModalOpen(true)}
            >
              Create New Harbor Request
            </button>
          ) : null}
        </div>
      ) : hasData ? (
        <DataGrid
          key={`${debouncedSearchTerm}-${statusFilter}-${pagination.pageIndex}-${pagination.pageSize}`}
          columns={columns}
          data={associationData.data}
          loading={
            isAssociationsLoading ||
            isFetching ||
            isCreating ||
            isCancelling ||
            isApproving ||
            isRevoking
          }
          pagination={{
            page: pagination.pageIndex,
            size: pagination.pageSize,
            more: true,
            totalItems,
            totalPages,
            setPagination,
            info: `{from} - {to} of {count}`,
            sizes: [5, 10, 25, 50, 100],
            sizesLabel: 'Show',
            sizesDescription: 'per page',
          }}
          sorting={[{ id: 'created_at', desc: true }]}
          serverSide={true}
          hasActiveFilters={hasActiveFilters}
          onFilterReset={handleFilterReset}
        />
      ) : (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-700"></div>
        </div>
      )}

      <HarborRequestModal
        open={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        onSubmit={handleNewRequest}
      />

      <HarborAssociationDetailsModal
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        associationData={selectedAssociation}
      />
    </Container>
  );
};

export default HarborAssociationListing;
